/**
 * Language detector for player chat messages and conversational inputs.
 * Detects whether player text is written in 'fr', 'en', 'es', 'de', 'nl', or 'pt'.
 * Returns null if the text is ambiguous, neutral, or too short.
 */

const DICTIONARIES = {
  fr: {
    words: new Set([
      'bonjour', 'salut', 'merci', 'oui', 'non', 'je', 'tu', 'il', 'elle', 'on', 'nous', 'vous', 'ils', 'elles',
      'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'est', 'suis', 'es', 'sommes', 'etes', 'êtes', 'sont',
      'pour', 'dans', 'avec', 'sur', 'mon', 'ma', 'mes', 'ton', 'ta', 'tes', 'son', 'sa', 'ses', 'notre', 'votre',
      'ce', 'cet', 'cette', 'ces', 'qui', 'que', 'quoi', 'comment', 'pourquoi', 'quand', 'tres', 'très', 'bien',
      'entrainement', 'entraînement', 'entrainer', 'entraîner', 'jouer', 'seance', 'séance', 'objectif', 'objectifs',
      'auto-evaluation', 'auto-évaluation', 'raquette', 'revers', 'coup', 'droit', 'service', 'remise', 'veux',
      'voudrais', 'peux', 'pouvez', 'sil', 'plaît', 'plait', 'salut', 'merci'
    ]),
    chars: /[éèêëàâîïôùûç]/i
  },
  en: {
    words: new Set([
      'hello', 'hi', 'hey', 'thanks', 'thank', 'yes', 'yeah', 'no', 'i', 'im', 'you', 'he', 'she', 'we', 'they',
      'the', 'a', 'an', 'is', 'am', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
      'for', 'in', 'with', 'on', 'at', 'to', 'from', 'my', 'your', 'his', 'her', 'our', 'their',
      'this', 'that', 'these', 'those', 'what', 'which', 'who', 'how', 'why', 'when', 'where', 'very', 'good',
      'training', 'practice', 'session', 'goal', 'goals', 'drill', 'drills', 'table', 'tennis',
      'backhand', 'forehand', 'serve', 'service', 'spin', 'loop', 'want', 'would', 'could', 'can', 'please'
    ]),
    chars: null
  },
  es: {
    words: new Set([
      'hola', 'gracias', 'si', 'sí', 'no', 'yo', 'tu', 'tú', 'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas',
      'es', 'soy', 'eres', 'somos', 'son', 'fue', 'era', 'para', 'por', 'en', 'con', 'sobre', 'mi', 'mis', 'su', 'sus',
      'nuestro', 'nuestra', 'este', 'esta', 'estos', 'estas', 'que', 'qué', 'quien', 'quién', 'como', 'cómo',
      'por que', 'porque', 'por qué', 'cuando', 'cuándo', 'muy', 'bien', 'entrenamiento', 'sesion', 'sesión',
      'objetivo', 'objetivos', 'autoevaluacion', 'autoevaluación', 'reves', 'revés', 'derecha', 'pala', 'paleta',
      'pelota', 'quiero', 'quisiera', 'puedo', 'puede', 'puedes', 'favor'
    ]),
    chars: /[áéíóúñ¿¡]/i
  },
  de: {
    words: new Set([
      'hallo', 'guten', 'tag', 'danke', 'ja', 'nein', 'ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr',
      'der', 'die', 'das', 'dem', 'den', 'des', 'ein', 'eine', 'einen', 'einem', 'einer', 'eines',
      'ist', 'bin', 'bist', 'sind', 'war', 'waren', 'fuer', 'für', 'in', 'mit', 'auf', 'aus', 'bei',
      'mein', 'meine', 'dein', 'deine', 'sein', 'seine', 'ihr', 'ihre', 'dieser', 'diese', 'dieses',
      'was', 'wie', 'warum', 'wann', 'wo', 'sehr', 'gut', 'training', 'uebung', 'übung', 'ziel', 'ziele',
      'rueckhand', 'rückhand', 'vorhand', 'aufschlag', 'bitte', 'moechte', 'möchte', 'kann', 'schlaeger', 'schläger'
    ]),
    chars: /[äöüß]/i
  },
  nl: {
    words: new Set([
      'hallo', 'hoi', 'dank', 'bedankt', 'ja', 'nee', 'ik', 'jij', 'je', 'hij', 'zij', 'ze', 'wij', 'we', 'jullie',
      'de', 'het', 'een', 'is', 'ben', 'bent', 'zijn', 'was', 'waren', 'voor', 'in', 'met', 'op', 'bij', 'van',
      'mijn', 'jouw', 'zijn', 'haar', 'ons', 'onze', 'dit', 'dat', 'deze', 'die', 'wat', 'hoe', 'waarom', 'wanneer',
      'erg', 'zeer', 'goed', 'oefening', 'oefeningen', 'doel', 'doelen', 'training', 'tafeltennis',
      'backhand', 'forehand', 'opslag', 'graag', 'alsjeblieft', 'kan', 'kunnen', 'wil', 'willen'
    ]),
    chars: null
  },
  pt: {
    words: new Set([
      'ola', 'olá', 'obrigado', 'obrigada', 'sim', 'nao', 'não', 'eu', 'tu', 'voce', 'você', 'ele', 'ela', 'nos', 'nós',
      'eles', 'elas', 'o', 'a', 'os', 'as', 'um', 'uma', 'uns', 'umas', 'e', 'é', 'sou', 'es', 'és', 'somos', 'sao', 'são',
      'para', 'pra', 'em', 'com', 'sobre', 'meu', 'minha', 'meus', 'minhas', 'seu', 'sua', 'seus', 'suas',
      'nosso', 'nossa', 'este', 'esta', 'esse', 'essa', 'que', 'quem', 'como', 'porque', 'por que', 'quando',
      'muito', 'muita', 'bem', 'treino', 'sessao', 'sessão', 'objetivo', 'objetivos', 'reves', 'revés', 'saque',
      'favor', 'quero', 'gostaria', 'posso', 'pode'
    ]),
    chars: /[ãõáéíóúâêôç]/i
  }
};

/**
 * Detect language from text string.
 * @param {string} text - User message
 * @param {string} [fallbackLang=null] - Optional fallback if undetermined
 * @param {number} [minConfidenceScore=2] - Minimum matched score to consider a language detected
 * @returns {string|null} - 'fr' | 'en' | 'es' | 'de' | 'nl' | 'pt' or fallbackLang
 */
export function detectInputLanguage(text, fallbackLang = null, minConfidenceScore = 2) {
  if (!text || typeof text !== 'string') return fallbackLang;
  const trimmed = text.trim();
  if (trimmed.length < 3) return fallbackLang;

  // Tokenize words (stripping punctuation except internal hyphens)
  const tokens = trimmed
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length === 0) return fallbackLang;

  const scores = { fr: 0, en: 0, es: 0, de: 0, nl: 0, pt: 0 };

  for (const token of tokens) {
    for (const [lang, dict] of Object.entries(DICTIONARIES)) {
      if (dict.words.has(token)) {
        scores[lang] += 2;
      }
    }
  }

  // Bonus points for characteristic characters
  for (const [lang, dict] of Object.entries(DICTIONARIES)) {
    if (dict.chars && dict.chars.test(trimmed)) {
      scores[lang] += 1;
    }
  }

  // Find language with highest score
  let maxLang = null;
  let maxScore = 0;
  let runnerUpScore = 0;

  for (const [lang, score] of Object.entries(scores)) {
    if (score > maxScore) {
      runnerUpScore = maxScore;
      maxScore = score;
      maxLang = lang;
    } else if (score > runnerUpScore) {
      runnerUpScore = score;
    }
  }

  // Need at least minConfidenceScore and a clear margin if there's a runner up
  if (maxScore >= minConfidenceScore && (maxScore > runnerUpScore || runnerUpScore === 0)) {
    return maxLang;
  }

  return fallbackLang;
}
